// FILE: src/components/case-studies/learner/caseRunnerUtils.js
// Pure logic for the learner case runner (PR 3). Unit-tested; the components
// stay thin. All server truths (reveal state, correctness) come from the
// RPCs — these helpers only organize what the server returned.

/** All questions across visible stages, ordered by case_sequence. */
export function visibleQuestions(stages) {
  return (stages || [])
    .flatMap((s) => (s.questions || []).map((q) => ({ ...q, stage_sequence: s.sequence })))
    .sort((a, b) => a.case_sequence - b.case_sequence);
}

/** First question without a response — the active cursor. Null when done. */
export function firstUnanswered(stages, responses) {
  const answered = new Set((responses || []).map((r) => r.case_sequence));
  return visibleQuestions(stages).find((q) => !answered.has(q.case_sequence)) || null;
}

/** Merge a server-returned unlocked stage into local stage state (idempotent). */
export function mergeUnlockedStage(stages, unlocked) {
  if (!unlocked) return stages;
  if (stages.some((s) => s.sequence === unlocked.sequence)) return stages;
  return [...stages, unlocked].sort((a, b) => a.sequence - b.sequence);
}

/**
 * Aggregate review breakdowns from responses joined to snapshot questions.
 * Returns { byDomain: [{key, correct, total}], byType: [...] }.
 */
export function buildBreakdowns(stages, responses) {
  const meta = new Map(visibleQuestions(stages).map((q) => [q.case_sequence, q]));
  const agg = (keyFn) => {
    const m = new Map();
    (responses || []).forEach((r) => {
      const q = meta.get(r.case_sequence) || {};
      const key = keyFn(q, r) || 'Unclassified';
      const cur = m.get(key) || { key, correct: 0, total: 0 };
      cur.total += 1;
      if (r.is_correct) cur.correct += 1;
      m.set(key, cur);
    });
    return Array.from(m.values()).sort((a, b) => a.key.localeCompare(b.key));
  };
  return {
    byDomain: agg((q) => q.performance_domain || q.eco_domain),
    byType: agg((q, r) => r.question_type || q.type),
  };
}

/** Initial draft answer for a question type (canonical array form). */
export function initialDraft(question) {
  if (question.type === 'ordered_sequence') {
    return (question.choices?.items || []).map((i) => String(i.id));
  }
  return [];
}

/** Is the draft submittable for this type? */
export function draftComplete(question, draft) {
  const d = draft || [];
  switch (question.type) {
    case 'multiple_select': return d.length >= 2;
    case 'enhanced_matching': return d.length === (question.choices?.prompts || []).length;
    case 'ordered_sequence': return d.length === (question.choices?.items || []).length;
    default: return d.length === 1;
  }
}

/** Progress counts for the header bar. */
export function progressOf(stages, responses, questionCount) {
  const answered = (responses || []).length;
  const total = questionCount || visibleQuestions(stages).length;
  return { answered, total, pct: total ? Math.round((100 * answered) / total) : 0 };
}
